import { useEffect, useId, useState, type FormEvent } from "react";
import { ArrowRight, Check, LoaderCircle, X } from "lucide-react";

type SubmissionState = "idle" | "submitting" | "success" | "error";

export function TrialApplicationCta() {
  const titleId = useId();
  const descriptionId = useId();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [state, setState] = useState<SubmissionState>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && state !== "submitting") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, state]);

  const begin = () => {
    setEmail("");
    setState("idle");
    setErrorMessage("");
    setOpen(true);
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setState("submitting");
    setErrorMessage("");
    try {
      const response = await fetch("/v1/trial-applications", {
        method: "POST",
        headers: { accept: "application/json", "content-type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() })
      });
      if (!response.ok) throw new Error("申请提交失败，请稍后重试");
      setState("success");
    } catch (error) {
      setState("error");
      setErrorMessage(error instanceof Error ? error.message : "申请提交失败，请稍后重试");
    }
  };

  return (
    <>
      <section className="trial-cta" aria-labelledby="trial-cta-title">
        <div className="trial-cta-inner">
          <p className="trial-cta-eyebrow">Matterline</p>
          <h2 id="trial-cta-title">把企业智能，留在企业内部。</h2>
          <button className="trial-cta-button" type="button" onClick={begin}>
            申请试用
            <ArrowRight aria-hidden="true" />
          </button>
        </div>
      </section>

      {open ? (
        <div
          className="trial-dialog-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && state !== "submitting") setOpen(false);
          }}
        >
          <div
            className="trial-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
          >
            <button
              className="trial-dialog-close"
              type="button"
              aria-label="关闭"
              title="关闭"
              disabled={state === "submitting"}
              onClick={() => setOpen(false)}
            >
              <X aria-hidden="true" />
            </button>

            {state === "success" ? (
              <div className="trial-dialog-success" role="status">
                <span className="trial-dialog-success-icon" aria-hidden="true">
                  <Check />
                </span>
                <h2 id={titleId}>申请已收到</h2>
                <p id={descriptionId}>我们会通过邮箱与你联系。</p>
                <button
                  className="trial-dialog-submit"
                  type="button"
                  onClick={() => setOpen(false)}
                >
                  完成
                </button>
              </div>
            ) : (
              <>
                <div className="trial-dialog-heading">
                  <p>MATTERLINE EARLY ACCESS</p>
                  <h2 id={titleId}>申请试用</h2>
                  <span id={descriptionId}>留下企业邮箱，获取 Matterline 私有部署试用资格。</span>
                </div>
                <form className="trial-dialog-form" onSubmit={(event) => void submit(event)}>
                  <label htmlFor="trial-email">邮箱</label>
                  <input
                    id="trial-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    maxLength={320}
                    required
                    autoFocus
                    placeholder="name@company.com"
                    value={email}
                    disabled={state === "submitting"}
                    aria-invalid={state === "error" || undefined}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                  {state === "error" ? <p className="trial-dialog-error">{errorMessage}</p> : null}
                  <button
                    className="trial-dialog-submit"
                    type="submit"
                    disabled={state === "submitting" || email.trim().length === 0}
                  >
                    {state === "submitting" ? (
                      <LoaderCircle className="trial-dialog-spinner" aria-hidden="true" />
                    ) : null}
                    提交申请
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}
